import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CheckCircle, AlertTriangle, Loader2, Trophy, X } from 'lucide-react';
import PenaltyCalculator from '../PenaltyCalculator';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Finish Event Modal — Quick entry mode for remaining athletes.
 * On confirm, calls POST /api/event-sessions/{id}/finish which:
 *  - applies penalty engine on quick entries
 *  - saves all completed passages as career performances
 *  - closes the event
 */
const FinishEventModal = ({ open, onClose, sessionId, federation = 'AIDA', eventTypeCode, onFinished, t }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState([]);
  const [entries, setEntries] = useState({}); // { passage_id: { realized_performance, performance_time_seconds, card_result, penalty_details, notes, score } }
  const [confirming, setConfirming] = useState(false);
  const [penaltyOpenFor, setPenaltyOpenFor] = useState(null);
  const isSTA = (eventTypeCode || '').toUpperCase() === 'STA';

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/remaining-passages`)
      .then(res => {
        setRemaining(res.data.remaining || []);
        const init = {};
        (res.data.remaining || []).forEach(p => {
          init[p.id] = { realized_performance: '', performance_time_seconds: '', card_result: 'white', penalty_details: {}, notes: '', score: null };
        });
        setEntries(init);
      })
      .catch(e => console.error('Failed to load remaining:', e))
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  const updateEntry = (passageId, patch) => {
    setEntries(prev => ({ ...prev, [passageId]: { ...prev[passageId], ...patch } }));
  };

  const recalcScore = async (passageId) => {
    const passage = remaining.find(p => p.id === passageId);
    const entry = entries[passageId] || {};
    if (!passage || !entry.realized_performance) {
      updateEntry(passageId, { score: null });
      return;
    }
    try {
      const announced = passage.registration?.announced_performance || 0;
      const pd = entry.penalty_details || {};
      let startDelay = null;
      if (pd.LATE) startDelay = pd.LATE.seconds || 0;
      else if (pd.EARLY) startDelay = -Math.abs(pd.EARLY.seconds || 0);
      const res = await axios.post(`${API_BASE_URL}/api/rules/calculate-penalties`, {
        federation,
        event_code: eventTypeCode,
        announced_performance: announced,
        realized_performance: parseFloat(entry.realized_performance) || 0,
        start_delay_seconds: startDelay,
        manual_penalties: pd
      });
      updateEntry(passageId, {
        score: res.data.net_points,
        is_dq: res.data.is_disqualified,
        suggested_card: res.data.card_suggestion
      });
    } catch (e) {
      console.error('Score calc failed:', e);
    }
  };

  const completedCount = useMemo(() =>
    Object.values(entries).filter(e => e.realized_performance !== '' && e.realized_performance != null).length,
    [entries]
  );

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const quick_entries = Object.entries(entries)
        .filter(([_, e]) => e.realized_performance !== '' && e.realized_performance != null)
        .map(([passage_id, e]) => ({
          passage_id,
          realized_performance: parseFloat(e.realized_performance) || 0,
          performance_time_seconds: e.performance_time_seconds ? parseFloat(e.performance_time_seconds) : null,
          card_result: e.card_result || 'white',
          penalty_details: e.penalty_details || {},
          notes: e.notes || ''
        }));
      const res = await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/finish`, { quick_entries });
      if (onFinished) onFinished(res.data);
      onClose();
    } catch (e) {
      console.error('Finish failed:', e);
      alert((t && t('errorFinishingEvent')) || 'Failed to finish event: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  const closePenalty = () => setPenaltyOpenFor(null);
  const applyPenalty = (passageId, penalties) => {
    updateEntry(passageId, { penalty_details: penalties });
    closePenalty();
    setTimeout(() => recalcScore(passageId), 50);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl bg-gradient-to-br from-indigo-950 to-blue-950 border-indigo-700/40 text-white max-h-[90vh] overflow-y-auto" data-testid="finish-event-modal">
        <DialogHeader>
          <DialogTitle className="text-indigo-200 flex items-center gap-2">
            <Trophy size={20} className="text-yellow-400" />
            {(t && t('finishEvent')) || 'Finish Event'}
          </DialogTitle>
          <p className="text-indigo-300 text-sm mt-1">{(t && t('quickEntryDesc')) || 'Enter results for the remaining athletes. Score is calculated automatically per agency rules (AIDA/CMAS).'}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin mr-2" /> {(t && t('loading')) || 'Loading...'}
          </div>
        ) : remaining.length === 0 ? (
          <div className="bg-emerald-900/30 border border-emerald-600/30 rounded-lg p-4 text-center">
            <CheckCircle className="mx-auto text-emerald-400 mb-2" size={32} />
            <p className="text-emerald-200">{(t && t('allAthletesCompleted')) || 'All athletes have completed their performances.'}</p>
            <p className="text-indigo-300 text-sm mt-2">{(t && t('clickFinishToValidate')) || 'Click "Finish & Save" to close the event and save all performances to career tracking.'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <Badge className="bg-amber-700/40 text-amber-200">
                <AlertTriangle size={12} className="mr-1" /> {remaining.length} {(t && t('athletesRemaining')) || 'remaining'}
              </Badge>
              <Badge className="bg-emerald-700/40 text-emerald-200">{completedCount} {(t && t('quickEntered')) || 'quick-entered'}</Badge>
              <Badge className="bg-indigo-700/40 text-indigo-200">{remaining.length - completedCount} {(t && t('toBeEntered')) || 'to be entered (will stay waiting)'}</Badge>
            </div>

            <div className="overflow-x-auto rounded-lg border border-indigo-700/30">
              <Table>
                <TableHeader className="bg-indigo-900/50">
                  <TableRow>
                    <TableHead className="text-indigo-200 w-10 text-xs">#</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{(t && t('fullName')) || 'Full Name'}</TableHead>
                    <TableHead className="text-indigo-200 text-xs">AP</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{isSTA ? 'RP (sec)' : 'RP (m)'}</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{(t && t('duration')) || 'Duration'} (sec)</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{(t && t('penalties')) || 'Penalties'}</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{(t && t('card')) || 'Card'}</TableHead>
                    <TableHead className="text-indigo-200 text-xs">{(t && t('score')) || 'Score'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {remaining.map(p => {
                    const fd = p.registration?.freediver || {};
                    const e = entries[p.id] || {};
                    const penaltyCount = Object.keys(e.penalty_details || {}).length;
                    return (
                      <TableRow key={p.id} className="border-indigo-800/40 hover:bg-indigo-900/30">
                        <TableCell className="text-indigo-300 font-mono text-xs">{p.passage_order}</TableCell>
                        <TableCell className="text-white text-sm">
                          <span className="font-semibold">{(fd.last_name || '').toUpperCase()}</span> {fd.first_name}
                        </TableCell>
                        <TableCell className="text-yellow-300 font-mono text-xs">{p.registration?.announced_performance ?? '-'}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder={isSTA ? '0' : '0'}
                            value={e.realized_performance ?? ''}
                            onChange={(ev) => updateEntry(p.id, { realized_performance: ev.target.value })}
                            onBlur={() => recalcScore(p.id)}
                            className="bg-indigo-950 border-indigo-700 text-white h-8 text-sm w-20"
                            data-testid={`quick-rp-${p.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="0"
                            value={e.performance_time_seconds ?? ''}
                            onChange={(ev) => updateEntry(p.id, { performance_time_seconds: ev.target.value })}
                            className="bg-indigo-950 border-indigo-700 text-white h-8 text-sm w-20"
                            data-testid={`quick-dur-${p.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`h-8 text-xs ${penaltyCount > 0 ? 'border-red-600/50 text-red-300 bg-red-900/20' : 'border-indigo-600/50 text-indigo-300'}`}
                            onClick={() => setPenaltyOpenFor(p.id)}
                            data-testid={`quick-penalties-${p.id}`}
                          >
                            {penaltyCount > 0 ? `${penaltyCount} ${(t && t('penalties')) || 'penalties'}` : ((t && t('addPenalty')) || 'Add')}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <select
                            value={e.card_result || 'white'}
                            onChange={(ev) => updateEntry(p.id, { card_result: ev.target.value })}
                            className="bg-indigo-950 border border-indigo-700 text-white rounded px-2 py-1 text-sm h-8"
                            data-testid={`quick-card-${p.id}`}
                          >
                            <option value="white">⚪ White</option>
                            <option value="yellow">🟡 Yellow</option>
                            <option value="red">🔴 Red</option>
                          </select>
                        </TableCell>
                        <TableCell>
                          {e.score != null ? (
                            <span className={`font-mono font-bold ${e.is_dq ? 'text-red-400' : 'text-emerald-300'}`} data-testid={`quick-score-${p.id}`}>
                              {e.is_dq ? 'DQ' : e.score.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-indigo-500 text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Penalty Calculator Modal — opens for current passage */}
        {penaltyOpenFor && (() => {
          const p = remaining.find(x => x.id === penaltyOpenFor);
          const e = entries[penaltyOpenFor] || {};
          const announced = p?.registration?.announced_performance || 0;
          return (
            <Dialog open={true} onOpenChange={(o) => !o && closePenalty()}>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl">
                <DialogHeader>
                  <DialogTitle className="text-blue-200 flex items-center justify-between">
                    {(t && t('penaltyCalculator')) || 'Penalty Calculator'} — {p?.registration?.freediver?.first_name} {p?.registration?.freediver?.last_name}
                    <button onClick={closePenalty} className="text-slate-400 hover:text-white"><X size={18} /></button>
                  </DialogTitle>
                </DialogHeader>
                <PenaltyCalculator
                  t={t}
                  federation={federation}
                  eventCode={eventTypeCode}
                  announced={announced}
                  realized={parseFloat(e.realized_performance) || 0}
                  performanceDuration={parseFloat(e.performance_time_seconds) || 0}
                  existingPenalties={Object.entries(e.penalty_details || {}).map(([code, data]) => {
                    if (code === 'LATE' || code === 'EARLY') return { code, value: (data || {}).seconds || 0 };
                    if (code === 'TURN') return { code: 'START_TURN', value: (data || {}).count || 0 };
                    if (code === 'GRAB') return { code: 'GRAB', value: (data || {}).count || 0 };
                    if (code === 'DOLPHIN_KICK') return { code: 'DOLPHIN_KICK', value: (data || {}).count || 0 };
                    return { code, value: 1 };
                  })}
                  onSave={(payload) => {
                    updateEntry(penaltyOpenFor, {
                      penalty_details: payload.penalty_details || {},
                      realized_performance: payload.distance_covered ?? e.realized_performance,
                      performance_time_seconds: payload.performance_time_seconds ?? e.performance_time_seconds,
                      card_result: payload.card_result || e.card_result || 'white',
                      score: payload.final_score,
                      is_dq: payload.is_dq
                    });
                    closePenalty();
                  }}
                />
              </DialogContent>
            </Dialog>
          );
        })()}

        <DialogFooter className="border-t border-indigo-800/40 pt-3 flex-col gap-3 sm:flex-row sm:justify-between">
          <p className="text-xs text-indigo-400 italic">
            {(t && t('willSaveCareerNote')) || 'All completed performances will be saved to athletes career tracking automatically.'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300" disabled={submitting} data-testid="finish-cancel-btn">
              {(t && t('cancel')) || 'Cancel'}
            </Button>
            {!confirming ? (
              <Button
                onClick={() => setConfirming(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={submitting}
                data-testid="finish-validate-btn"
              >
                <CheckCircle className="mr-2" size={16} />
                {(t && t('finishAndSave')) || 'Finish & Save'}
              </Button>
            ) : (
              <>
                <Button onClick={() => setConfirming(false)} variant="outline" className="border-amber-600 text-amber-300" disabled={submitting}>
                  {(t && t('back')) || 'Back'}
                </Button>
                <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-white" disabled={submitting} data-testid="finish-confirm-btn">
                  {submitting ? <Loader2 className="animate-spin mr-2" size={14} /> : <CheckCircle className="mr-2" size={16} />}
                  {(t && t('confirmFinishShort')) || 'Yes, Finish Event'}
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FinishEventModal;
